// Uložení a čtení souhlasu: first-party cookie + localStorage jako záloha.
import type { ConsentState, Lang, StoredConsent } from './types';

const KEY = 'cookiegobbler_consent';

/** Náhodné veřejné ID souhlasu (prokazatelnost). */
function genId(): string {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch {
    /* fallback níže */
  }
  return 'cg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function writeCookie(value: string, days: number): void {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${KEY}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies nedostupné (sandbox/blokované) — localStorage stačí */
  }
}

function readCookie(): string | null {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + KEY + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function saveConsent(
  state: ConsentState,
  version: string,
  lang: Lang,
  expiryDays: number,
): StoredConsent {
  const record: StoredConsent = {
    id: genId(),
    version,
    timestamp: Date.now(),
    state,
    lang,
  };
  const json = JSON.stringify(record);
  writeCookie(json, expiryDays);
  try {
    localStorage.setItem(KEY, json);
  } catch {
    /* localStorage nedostupné (private mode) — cookie stačí */
  }
  return record;
}

/**
 * Načte uložený souhlas. Vrátí null, pokud chybí, je z jiné verze konfigurace,
 * nebo už vypršel (kontrola i pro localStorage, kde cookie expiry neplatí).
 */
export function loadConsent(
  version: string,
  expiryDays: number,
): StoredConsent | null {
  const raw = readCookie() ?? safeLocalGet();
  if (!raw) return null;
  try {
    const record = JSON.parse(raw) as StoredConsent;
    if (record.version !== version) return null;
    if (Date.now() - record.timestamp > expiryDays * 864e5) return null;
    return record;
  } catch {
    return null;
  }
}

function safeLocalGet(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearConsent(): void {
  try {
    document.cookie = `${KEY}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
